/**
 * Fleet Manager HQ — replaces the old Pricing HQ route at /pricing-hq.
 * Modeled on the user's ACE_Fleet_Manager.html artifact:
 *   - Stats tiles (Total / Active / In Service / Exotic / Starting Rate / Peak Rate)
 *   - Filter chips (All / Ownership / Class / Status)
 *   - Vehicle cards grouped by 4 categories (Sedans · SUVs · Group · Exotic)
 *   - Inline rate edit (retail / pax / min hours / features / color)
 *   - Exotic = "Retail only — no member discounts"
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Plus, Pencil, Search, X } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Tier discount multipliers (matches the artifact's display: Sta 95%, Plu 91%, Pre 85%, Ess 80%, Exe 75%, Bla 71%)
const TIERS = [
  { key: 'sta', label: 'Sta', mult: 0.95 },
  { key: 'plu', label: 'Plu', mult: 0.91 },
  { key: 'pre', label: 'Pre', mult: 0.85 },
  { key: 'ess', label: 'Ess', mult: 0.80 },
  { key: 'exe', label: 'Exe', mult: 0.75 },
  { key: 'bla', label: 'Bla', mult: 0.71 },
];

const CATEGORIES = [
  { key: 'concierge_sedan', label: 'First-Class Sedans',   number: '01', match: v => v.category === 'concierge' && /sedan|s-class|maybach|spur|ghost|ct\d|model/i.test(v.vehicle_name) },
  { key: 'concierge_suv',   label: 'First-Class SUVs',     number: '02', match: v => v.category === 'concierge' && /suv|escalade|navigator|yukon|tahoe|gle|gls|range|x7|bentayga|cullinan/i.test(v.vehicle_name) },
  { key: 'group',           label: 'Group & Event Transport', number: '03', match: v => v.category === 'group' },
  { key: 'exotic',          label: 'Exotic Collection',     number: '04', match: v => v.category === 'exotic' },
];

const FILTERS = [
  { key: 'all',      label: 'All Fleet' },
  { key: 'ace_owned',label: 'ACE Owned' },
  { key: 'rentals',  label: 'Driving - Rentals' },
  { key: 'sedans',   label: 'Sedans' },
  { key: 'suvs',     label: 'SUVs' },
  { key: 'group',    label: 'Group' },
  { key: 'exotic',   label: 'Exotic' },
  { key: 'active',   label: 'Active' },
];

const moneyR = (n) => `$${Math.round(Number(n || 0)).toLocaleString()}`;

const FleetManagerHQ = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/pricing/vehicles`);
        const d = await r.json();
        if (!cancelled) setVehicles((d.vehicles || []).filter(v => !v.is_deleted));
      } catch (e) { toast.error('Failed to load fleet'); }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [reloadTick]);

  // Filter pipeline
  const filtered = useMemo(() => {
    let list = vehicles;
    if (q) {
      const k = q.toLowerCase();
      list = list.filter(v => (v.vehicle_name || '').toLowerCase().includes(k) || (v.category || '').toLowerCase().includes(k));
    }
    switch (filter) {
      case 'ace_owned': list = list.filter(v => (v.ownership || v.ownership_type || '').toLowerCase().includes('owned') || (v.tag || '').toLowerCase() === 'ace'); break;
      case 'rentals':   list = list.filter(v => v.category === 'rental' || (v.tag || '').toLowerCase().includes('rental')); break;
      case 'sedans':    list = list.filter(v => /sedan|s-class|maybach|spur|ghost|ct\d|model y/i.test(v.vehicle_name || '')); break;
      case 'suvs':      list = list.filter(v => /suv|escalade|navigator|yukon|tahoe|gle|gls|range|x7|bentayga|cullinan|urus/i.test(v.vehicle_name || '')); break;
      case 'group':     list = list.filter(v => v.category === 'group'); break;
      case 'exotic':    list = list.filter(v => v.category === 'exotic'); break;
      case 'active':    list = list.filter(v => v.status !== 'inactive' && v.status !== 'in_service'); break;
      default: break;
    }
    return list;
  }, [vehicles, q, filter]);

  // Stats
  const stats = useMemo(() => {
    const total = filtered.length;
    const active = filtered.filter(v => v.status !== 'inactive' && v.status !== 'in_service').length;
    const inService = filtered.filter(v => v.status === 'in_service').length;
    const exotic = filtered.filter(v => v.category === 'exotic').length;
    const rates = filtered.map(v => Number(v.retail_hourly) || 0).filter(Boolean);
    return {
      total, active, inService, exotic,
      startingRate: rates.length ? Math.min(...rates) : 0,
      peakRate:    rates.length ? Math.max(...rates) : 0,
    };
  }, [filtered]);

  // Group by category for display
  const grouped = useMemo(() => CATEGORIES.map(c => ({ ...c, items: filtered.filter(v => c.match(v)) })), [filtered]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-black" data-testid="fleet-manager-hq">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200 px-6 py-3 flex items-center gap-4">
        <button onClick={() => navigate('/admin')} className="text-gray-500 hover:text-black"><ArrowLeft className="h-4 w-4" /></button>
        <div className="flex-1">
          <p className="text-[9px] text-red-600 font-bold tracking-[0.3em] uppercase">ACE</p>
          <h1 className="text-base font-black tracking-tight">Fleet Manager</h1>
        </div>
        <button className="text-[11px] font-bold uppercase tracking-wider text-gray-600 hover:text-black px-3 py-1.5 border border-gray-300 rounded inline-flex items-center gap-1.5" data-testid="export-btn"><Download className="h-3 w-3" />Export</button>
        <button onClick={() => setEditing({})} className="text-[11px] font-bold uppercase tracking-wider text-white bg-red-600 hover:bg-red-700 px-3 py-1.5 rounded inline-flex items-center gap-1.5" data-testid="add-vehicle-btn"><Plus className="h-3 w-3" />Add Vehicle</button>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats row */}
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-5" data-testid="fleet-stats">
          <Stat label="Total"         value={stats.total} />
          <Stat label="Active"        value={stats.active} accent="#15803D" />
          <Stat label="In Service"    value={stats.inService} accent="#B45309" />
          <Stat label="Exotic"        value={stats.exotic} accent="#A8862F" />
          <Stat label="Starting Rate" value={moneyR(stats.startingRate)} />
          <Stat label="Peak Rate"     value={moneyR(stats.peakRate)} accent="#CC0000" />
        </div>

        {/* Filter chips + search */}
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search vehicle…" className="w-full h-9 pl-9 pr-3 border border-gray-300 rounded text-xs" data-testid="fleet-search" />
          </div>
          {FILTERS.map(f => (
            <button key={f.key} onClick={() => setFilter(f.key)} className={`px-3 h-9 text-[10px] font-bold uppercase tracking-wider rounded transition-colors ${filter === f.key ? 'bg-red-600 text-white border border-red-600' : 'bg-white text-gray-600 border border-gray-300 hover:border-gray-500'}`} data-testid={`filter-${f.key}`}>{f.label}</button>
          ))}
        </div>

        {loading && <p className="text-xs text-gray-400 text-center py-12" data-testid="fleet-loading">Loading fleet…</p>}

        {/* Category groups */}
        {!loading && grouped.map(g => {
          if (g.items.length === 0) return null;
          const isExotic = g.key === 'exotic';
          return (
            <section key={g.key} className="mb-8" data-testid={`category-${g.key}`}>
              <div className="flex items-baseline gap-3 mb-3">
                <span className="text-[10px] font-mono text-gray-400 tracking-widest">{g.number}</span>
                <h2 className="text-base font-bold text-black">{g.label}</h2>
                {isExotic && <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-50 px-2 py-0.5 rounded">Category 04 · No Discounts</span>}
              </div>
              {isExotic && (
                <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs px-3 py-2 rounded mb-3 flex items-center gap-2">
                  <span className="text-amber-700 text-base">♠</span>
                  <span><strong>STRICT POLICY:</strong> No discounts on Exotic Collection — all tiers pay full retail.</span>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {g.items.map(v => <VehicleCard key={v.vehicle_id} v={v} isExotic={isExotic} onEdit={() => setEditing(v)} />)}
              </div>
            </section>
          );
        })}

        {!loading && filtered.length === 0 && (
          <div className="border border-dashed border-gray-300 text-gray-400 text-sm text-center py-16 rounded" data-testid="fleet-empty">No vehicles match your filter.</div>
        )}

        <div className="text-center mt-6">
          <button onClick={() => setEditing({})} className="text-xs text-red-600 font-bold uppercase tracking-wider hover:text-red-700 inline-flex items-center gap-1.5" data-testid="add-vehicle-footer"><Plus className="h-3 w-3" />Add Vehicle to Fleet</button>
        </div>
      </main>

      {editing && <VehicleEditor vehicle={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); setReloadTick(t => t + 1); }} />}
    </div>
  );
};

// ─── Vehicle Card ─────────────────────────────────────────────────────
const VehicleCard = ({ v, isExotic, onEdit }) => {
  const retail = Number(v.retail_hourly) || 0;
  const halfDay = Math.round(retail * 3.6);
  const daily = Math.round(retail * 6.8);
  const ownership = v.ownership || (v.tag === 'rental' || v.category === 'rental' ? 'Driving - Rentals' : v.tag === 'ace_owned' ? 'ACE Owned' : isExotic ? 'Exotic' : 'Brokered');

  return (
    <div className="bg-white border border-gray-200 rounded-md overflow-hidden hover:border-gray-400 transition-colors flex flex-col" data-testid={`vehicle-card-${v.vehicle_id}`}>
      <div className="aspect-[4/3] bg-gray-100 relative flex items-center justify-center">
        {v.image_url ? (
          <img src={v.image_url} alt={v.vehicle_name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="text-center text-gray-400">
            <span className={`text-3xl ${isExotic ? 'text-amber-600' : ''}`}>{isExotic ? '✦' : '🚗'}</span>
            <p className="text-[10px] mt-1">No photo</p>
          </div>
        )}
      </div>
      <div className="p-3 flex-1 flex flex-col">
        <p className="text-sm font-bold text-black truncate">{v.vehicle_name}</p>
        <p className="text-[10px] text-gray-500 mt-0.5">{v.status || 'active'} · {v.pax || '—'} pax · Min {v.min_hours || 3} hrs</p>
        <p className="text-[10px] text-gray-400 uppercase tracking-wider mt-0.5">{ownership}</p>

        <div className="mt-2.5 flex items-baseline gap-1">
          <span className="text-lg font-black text-red-600">{moneyR(retail)}</span>
          <span className="text-[10px] text-gray-400">/ hr retail</span>
        </div>
        <p className="text-[10px] text-gray-500">Half-day {moneyR(halfDay)} · Daily {moneyR(daily)}</p>

        {isExotic ? (
          <p className="text-[10px] text-amber-700 mt-2 font-bold">♠ Retail only — no member discounts</p>
        ) : (
          <div className="mt-2 flex flex-wrap gap-1" data-testid={`tiers-${v.vehicle_id}`}>
            {TIERS.map(t => (
              <span key={t.key} className="text-[9px] text-gray-600 bg-gray-100 px-1.5 py-0.5 rounded font-mono">{t.label} {moneyR(retail * t.mult)}</span>
            ))}
          </div>
        )}

        <div className="mt-2 text-[10px] text-gray-500 line-clamp-2" title={(v.features || []).join(', ')}>{(v.features || []).slice(0, 3).join(' · ') || (v.tag ? `Tag: ${v.tag}` : '—')}</div>

        <button onClick={onEdit} className="mt-3 text-[10px] font-bold uppercase tracking-wider text-red-600 hover:text-red-700 inline-flex items-center gap-1" data-testid={`edit-${v.vehicle_id}`}><Pencil className="h-3 w-3" />Edit Profile &amp; Pricing</button>
      </div>
    </div>
  );
};

const Stat = ({ label, value, accent = '#000' }) => (
  <div className="bg-white border border-gray-200 rounded p-2.5 text-center">
    <p className="text-[10px] text-gray-500 uppercase tracking-[0.15em] font-bold">{label}</p>
    <p className="text-lg font-black font-mono" style={{ color: accent }}>{value}</p>
  </div>
);

// ─── Vehicle Editor Drawer ────────────────────────────────────────────
const VehicleEditor = ({ vehicle, onClose, onSaved }) => {
  const isNew = !vehicle.vehicle_id;
  const [draft, setDraft] = useState({
    vehicle_name: vehicle.vehicle_name || '',
    category: vehicle.category || 'concierge',
    retail_hourly: vehicle.retail_hourly || 0,
    member_hourly: vehicle.member_hourly || 0,
    affiliate_hourly: vehicle.affiliate_hourly || 0,
    pax: vehicle.pax || 4,
    min_hours: vehicle.min_hours || 3,
    status: vehicle.status || 'active',
    tag: vehicle.tag || '',
    image_url: vehicle.image_url || '',
    features: (vehicle.features || []).join('\n'),
    color: vehicle.color || 'Black',
  });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!draft.vehicle_name.trim()) { toast.error('Vehicle name required'); return; }
    setBusy(true);
    try {
      const payload = {
        ...draft,
        retail_hourly: Number(draft.retail_hourly),
        member_hourly: Number(draft.member_hourly),
        affiliate_hourly: Number(draft.affiliate_hourly),
        pax: Number(draft.pax),
        min_hours: Number(draft.min_hours),
        features: draft.features.split('\n').map(s => s.trim()).filter(Boolean),
      };
      const url = isNew ? `${API}/api/pricing/vehicles` : `${API}/api/pricing/vehicles/${vehicle.vehicle_id}`;
      const r = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Save failed');
      toast.success(isNew ? 'Vehicle added' : 'Vehicle updated');
      onSaved();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm(`Delete ${vehicle.vehicle_name}?`)) return;
    try {
      const r = await fetch(`${API}/api/pricing/vehicles/${vehicle.vehicle_id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Delete failed');
      toast.success('Vehicle removed');
      onSaved();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={onClose} data-testid="vehicle-editor">
      <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-3 flex items-center justify-between">
          <div>
            <p className="text-[9px] text-red-600 font-bold uppercase tracking-[0.3em]">{isNew ? 'New Vehicle' : 'Vehicle Profile'}</p>
            <h3 className="text-base font-bold text-black mt-0.5">{draft.vehicle_name || 'Untitled'}</h3>
          </div>
          <button onClick={onClose}><X className="h-4 w-4 text-gray-400" /></button>
        </div>

        <div className="p-5 space-y-3 text-xs">
          <Field label="Vehicle Name *"><input value={draft.vehicle_name} onChange={e => setDraft({ ...draft, vehicle_name: e.target.value })} className={inputCls} data-testid="vh-name" /></Field>
          <Field label="Image URL"><input value={draft.image_url} onChange={e => setDraft({ ...draft, image_url: e.target.value })} placeholder="https://…" className={inputCls} data-testid="vh-image" /></Field>
          <Field label="Category">
            <select value={draft.category} onChange={e => setDraft({ ...draft, category: e.target.value })} className={inputCls} data-testid="vh-category">
              <option value="concierge">Concierge (Sedan / SUV)</option>
              <option value="group">Group &amp; Event Transport</option>
              <option value="exotic">Exotic Collection</option>
              <option value="rental">Driving - Rentals</option>
            </select>
          </Field>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Pax"><input type="number" value={draft.pax} onChange={e => setDraft({ ...draft, pax: e.target.value })} className={inputCls} data-testid="vh-pax" /></Field>
            <Field label="Min Hours"><input type="number" value={draft.min_hours} onChange={e => setDraft({ ...draft, min_hours: e.target.value })} className={inputCls} data-testid="vh-min" /></Field>
            <Field label="Status">
              <select value={draft.status} onChange={e => setDraft({ ...draft, status: e.target.value })} className={inputCls} data-testid="vh-status">
                <option value="active">Active</option>
                <option value="in_service">In Service</option>
                <option value="inactive">Inactive</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Retail $/hr"><input type="number" value={draft.retail_hourly} onChange={e => setDraft({ ...draft, retail_hourly: e.target.value })} className={inputCls} data-testid="vh-retail" /></Field>
            <Field label="Member $/hr"><input type="number" value={draft.member_hourly} onChange={e => setDraft({ ...draft, member_hourly: e.target.value })} className={inputCls} data-testid="vh-member" /></Field>
            <Field label="Affiliate $/hr"><input type="number" value={draft.affiliate_hourly} onChange={e => setDraft({ ...draft, affiliate_hourly: e.target.value })} className={inputCls} data-testid="vh-affiliate" /></Field>
          </div>
          <Field label="Color"><input value={draft.color} onChange={e => setDraft({ ...draft, color: e.target.value })} className={inputCls} data-testid="vh-color" /></Field>
          <Field label="Tag (e.g. ace_owned, rental)"><input value={draft.tag} onChange={e => setDraft({ ...draft, tag: e.target.value })} className={inputCls} data-testid="vh-tag" /></Field>
          <Field label="Features (one per line)"><textarea value={draft.features} onChange={e => setDraft({ ...draft, features: e.target.value })} rows={5} className={`${inputCls} font-mono`} data-testid="vh-features" /></Field>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-200 px-5 py-3 flex gap-2">
          {!isNew && <button onClick={remove} className="px-3 h-9 text-[10px] font-bold uppercase tracking-wider border border-red-500 text-red-600 hover:bg-red-50 rounded" data-testid="vh-delete">Delete</button>}
          <div className="flex-1" />
          <button onClick={onClose} className="px-3 h-9 text-[10px] font-bold uppercase tracking-wider border border-gray-300 text-gray-600 hover:bg-gray-50 rounded" data-testid="vh-cancel">Cancel</button>
          <button onClick={submit} disabled={busy} className="px-4 h-9 text-[10px] font-bold uppercase tracking-wider bg-red-600 hover:bg-red-700 text-white rounded disabled:opacity-50 inline-flex items-center gap-1" data-testid="vh-save">♠ {busy ? 'Saving…' : 'Save Changes'}</button>
        </div>
      </div>
    </div>
  );
};

const inputCls = "w-full h-9 px-2 border border-gray-300 rounded text-xs focus:outline-none focus:border-red-500";
const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] text-gray-500 uppercase tracking-wider font-bold mb-1">{label}</label>
    {children}
  </div>
);

export default FleetManagerHQ;
